import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName } from '../MenuItemTags';
import { isMenuItemActive, resolveItemImageUrl } from '../menuEditHelpers';
import { readHighlightMenuSection } from './highlightMenuItems';

/**
 * Highlight dish cards — item-first photo grid (no category bands, no Hinweise).
 * Full Speisekarte dish cards stay on `MenuDishCards` / `Static_Menu_Dish_Cards`.
 */
export function MenuHighlightDishCards({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const { sectionTitle, sectionSubtitle, dishes } = readHighlightMenuSection(block, locale);
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-hl-dishes', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-hl-dishes__inner">
        <header className="ws-hl-dishes__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-hl-dishes__title"
          >
            {sectionTitle}
          </EditableContent>
          {sectionSubtitle || editable ? (
            <EditableContent
              as="p"
              fieldKey="sectionSubtitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-hl-dishes__subtitle"
            >
              {sectionSubtitle}
            </EditableContent>
          ) : null}
        </header>
        {dishes.length > 0 ? (
          <ul className="ws-hl-dishes__grid">
            {dishes.map(({ item, columnIndex, itemIndex }) => {
              const itemActive = isMenuItemActive(
                target?.kind === 'menu-item' ? target : null,
                columnIndex,
                itemIndex,
              );
              const imageUrl = resolveItemImageUrl(block, item, columnIndex, itemIndex);
              const imageFieldKey = `itemImage_${columnIndex}_${itemIndex}`;
              const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : undefined;
              const imageSrcSet = imageUrl
                ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900], 78)
                : undefined;

              return (
                <li key={`${columnIndex}-${itemIndex}`}>
                  <article
                    className={['ws-hl-dishes__card', itemActive ? 'ws-edit-child--active' : '']
                      .filter(Boolean)
                      .join(' ')}
                    {...(editable
                      ? {
                          'data-edit-child': 'menu-item' as const,
                          'data-column-index': columnIndex,
                          'data-item-index': itemIndex,
                        }
                      : {})}
                  >
                    <div className="ws-hl-dishes__media">
                      {imageUrl ? (
                        <EditableMedia
                          fieldKey={imageFieldKey}
                          editable={editable}
                          activeEditTarget={target}
                          className="ws-hl-dishes__media-frame"
                          src={imageSrc}
                          srcSet={imageSrcSet}
                          sizes={SITE_IMAGE_SIZES.menuDishCard}
                          alt={item.name || 'Dish'}
                          width={720}
                          height={720}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="ws-hl-dishes__media-placeholder" aria-hidden />
                      )}
                    </div>
                    <div className="ws-hl-dishes__body">
                      <div className="ws-hl-dishes__head">
                        <MenuItemName
                          item={item}
                          locale={locale}
                          className="ws-hl-dishes__name"
                          showLegalMarks={false}
                          showOrderCode={false}
                        />
                        <MenuItemDietaryTags
                          item={item}
                          locale={locale}
                          iconsOnly
                          placement="inline"
                        />
                      </div>
                      {item.description ? (
                        <p className="ws-hl-dishes__desc">{item.description}</p>
                      ) : null}
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
